import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Divider, IconButton } from "@evergis/ui";
import { useMapViewActions } from "@evergis/react";

import { MapControlContainer } from "../../styled";
import { CollapsableContainer, ExpanderContainer } from "../../../Map/components/GeometrySelectionTools/styled";
import { MeasureControlButton } from "../MeasureControl/MeasureControlButton";
import { GeoLocationButton } from "../GeoLocation/GeoLocationButton";
import { AddressSearchButton } from "../AddressSearch/AddressSearchButton";
import { ZoomLevel } from "./ZoomLevel";
import { castScaleToResolution } from "../../../../evergis/core/scale";
import { useDefaultScaleRange } from "../../../../hooks/style/useDefaultScaleRange";
import { useMapView } from "../../../../ducks/mapView/useMapView";
import { usePresentationMode } from "../../../../hooks/layout/usePresentationMode";
import { useToggle } from "../../../../hooks/useToggle";

export const ScaleControl: FC = () => {
  const { t } = useTranslation("mapControls");
  const { zoomIn, zoomOut } = useMapViewActions();
  const [{ resolution }] = useMapView();
  const defaultScaleRange = useDefaultScaleRange();
  const [minResolution, maxResolution] = castScaleToResolution(defaultScaleRange);
  const [collapsed, toggleCollapsed] = useToggle(true);
  const { isPresentationMode } = usePresentationMode();

  const onZoomIn = useCallback(() => {
    if (resolution > minResolution) {
      zoomIn();
    }
  }, [minResolution, resolution, zoomIn]);

  const onZoomOut = useCallback(() => {
    if (resolution < maxResolution) {
      zoomOut();
    }
  }, [maxResolution, resolution, zoomOut]);

  return (
    <MapControlContainer>
      <CollapsableContainer collapsed={collapsed} $width={"6.5rem"}>
        <IconButton title={t("zoomIn")} kind={"plus"} onClick={onZoomIn} />
        <ZoomLevel />
        <IconButton title={t("zoomOut")} kind={"minus"} onClick={onZoomOut} />
        {isPresentationMode && (
          <>
            <MeasureControlButton />
            <GeoLocationButton />
            <AddressSearchButton />
          </>
        )}
      </CollapsableContainer>
      {isPresentationMode && (
        <ExpanderContainer>
          <Divider vertical />
          <IconButton kind={collapsed ? "next" : "prev"} onClick={toggleCollapsed} />
        </ExpanderContainer>
      )}
    </MapControlContainer>
  );
};
